import { Stack, Typography, Button, Chip } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  OrderCardContainer,
  OrderCardContent,
  OrderCardStatus,
} from './order-card.styles'
import { useMemo, useState } from 'react'
import TranspotsAlert from '../../transpots-alert/transpots-alert.component'
import CircleGreenRevised from '../../../assets/icons/Circle Green Revised.png'
import { Order } from '../../../store/orders/orders.types'
import { useDispatch, useSelector, useStore } from 'react-redux'
import {
  setSelectedCompany,
  setSelectedOrder,
  setIsSelectedOrderLoading,
  clearSelectedOrder,
} from '../../../store/orders/orders.slice'
import { useLazyGetCompanyByIdQuery } from '../../../store/company/company.api'
import {
  useLazyGetOrderByIdQuery,
  useLazyListNewOrdersQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useLazyListActiveOrdersQuery,
  useLazyListArchivedOrdersQuery,
} from '../../../store/orders/orders.api'
import { RootState } from '../../../store'
import { useNavigate } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import ConfirmationDialog from '../../confirmation-dialog/confirmation-dialog.component'
import { getRole } from 'utils/common'
import { useDeletePendingLoadConfirmationMutation } from 'store/task-automation/task-automation.api'
import { DateTime } from 'luxon'

interface OrderCardProps {
  order: Order
  selectedOrder: Order
  setIsOrderSelectedManually: (value: boolean) => void
  onRepeatOrder?: (order: Order) => void
}

const OrderCard = ({
  order,
  selectedOrder,
  setIsOrderSelectedManually,
  onRepeatOrder,
}: OrderCardProps) => {
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  const isPendingLcDoc = order?.isTempLcDoc

  const selectedOrderTab = useSelector(
    (state: RootState) => state.orders.selectedOrderTab
  )

  const [getOrderById] = useLazyGetOrderByIdQuery()
  const [getCompanyById] = useLazyGetCompanyByIdQuery()
  const [updateOrderStatus] = useUpdateOrderStatusMutation()
  const [deleteOrderById, { isError }] = useDeleteOrderMutation()
  const [deletePendingLc] = useDeletePendingLoadConfirmationMutation()
  const navigate = useNavigate()
  const [refetchNewOrders] = useLazyListNewOrdersQuery()
  const [refetchActiveOrders] = useLazyListActiveOrdersQuery()
  const [refetchArchiveOrders] = useLazyListArchivedOrdersQuery()

  const listParams = { page: 0, size: 10 }

  const refetchCurrentTabOrders = () => {
    if (selectedOrderTab === 'NEW') {
      void refetchNewOrders(listParams)
    } else if (
      selectedOrderTab === 'ASSIGNED' ||
      selectedOrderTab === 'ACTIVE'
    ) {
      void refetchActiveOrders(listParams)
    } else if (
      selectedOrderTab === 'DELIVERED' ||
      selectedOrderTab === 'ARCHIVED'
    ) {
      void refetchArchiveOrders(listParams)
    }
  }

  const [alertVisible, setAlertVisible] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const role = getRole()
  const isDispatch = role === 'DISPATCH'

  const isSelectedOrder = useMemo(
    () => order?.orderId === selectedOrder?.orderId,
    [order, selectedOrder]
  )

  const displayStatus = useMemo(() => {
    if (selectedOrderTab === 'DELIVERED' || selectedOrderTab === 'ARCHIVED') {
      return 'DELIVERED'
    }

    if (selectedOrderTab === 'ASSIGNED' && isDispatch) {
      return 'ASSIGNED'
    }

    return order?.status
  }, [selectedOrderTab, order?.status, isDispatch])

  const isFailedFile = useMemo(() => {
    const fileName = order?.fileName || order?.customerName || ''
    return fileName.startsWith('FAILED_')
  }, [order?.fileName, order?.customerName])

  const displayCustomerName = useMemo(() => {
    const customerName = order?.customerName || order?.fileName || ''
    if (isFailedFile) {
      return customerName.replace(/^FAILED_/, '')
    }
    return customerName
  }, [order?.customerName, order?.fileName, isFailedFile])

  const displayCreatedDate = useMemo(() => {
    if (!order?.createdDate) return ''
    const dt = DateTime.fromISO(order.createdDate, { zone: 'utc' }).toLocal()
    if (!dt.isValid) return ''
    return dt.toFormat('LLL dd, yyyy')
  }, [order?.createdDate])

  const handleAlertClose = () => {
    setAlertVisible(false)
  }

  const handleEditOrder = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/order/verify/${order.orderId}`)
  }

  const handleOrderSelect = async () => {
    if (isPendingLcDoc) {
      setIsOrderSelectedManually(true)
      dispatch(setIsSelectedOrderLoading(false))
      dispatch(setSelectedOrder({ selectedOrder: order }))
      return
    }

    const requestedOrderId = order.orderId
    setIsOrderSelectedManually(true)
    // Select immediately so the card highlights while details load
    dispatch(setSelectedOrder({ selectedOrder: order }))
    dispatch(setIsSelectedOrderLoading(true))

    try {
      const { data } = await getOrderById({ orderId: requestedOrderId })

      // Ignore stale responses if the user selected another order
      if (store.getState().orders.selectedOrder?.orderId !== requestedOrderId) {
        return
      }

      if (!data) {
        dispatch(clearSelectedOrder())
        return
      }

      dispatch(setSelectedOrder({ selectedOrder: data }))

      if (data.companyId) {
        const { data: companyData } = await getCompanyById({ id: data.companyId })
        if (store.getState().orders.selectedOrder?.orderId !== requestedOrderId) {
          return
        }
        if (companyData) {
          dispatch(setSelectedCompany({ selectedCompany: companyData }))
        }
      }
    } catch (error) {
      console.error('Error while selecting order', error)
      if (store.getState().orders.selectedOrder?.orderId === requestedOrderId) {
        dispatch(clearSelectedOrder())
      }
    } finally {
      if (store.getState().orders.selectedOrder?.orderId === requestedOrderId) {
        dispatch(setIsSelectedOrderLoading(false))
      }
    }
  }

  const handleAcceptOrder = async () => {
    try {
      await updateOrderStatus({ orderId: order?.orderId, status: 'ACTIVE' })
      handleAlertClose()
      await refetchNewOrders(listParams)
    } catch (error) {
      console.log(`Error on update order status ${error}`)
    }
  }

  const handleVerifyOrder = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`verify/${order?.orderId}`)
  }

  const handleDeleteOrder = async () => {
    try {
      await deleteOrderById({
        orderId: order?.orderId.toString() ?? skipToken,
      })

      if (!isError) {
        refetchCurrentTabOrders()
        setDeleteOpen(false)
        dispatch(clearSelectedOrder())
      }
    } catch (error) {
      console.error('Error while deleting order', error)
    }
  }

  const handleDeletePendingDoc = async (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!order?.s3Key) return

    try {
      await deletePendingLc({ s3Key: order.s3Key }).unwrap()
    } catch (error) {
      console.error('Error while deleting pending load confirmation', error)
    }
  }

  const handleRepeatOrder = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRepeatOrder?.(order)
  }

  const actionButtonRowSx = {
    padding: '8px 12px 12px',
    bgcolor: isSelectedOrder ? '#E8F3FC' : 'common.white',
  } as const

  const verifyButtonSx = {
    flex: 1,
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    paddingX: '10px',
    paddingY: '6px',
  } as const

  return (
    <OrderCardContainer $selected={isSelectedOrder} onClick={handleOrderSelect}>
      <OrderCardContent $selected={isSelectedOrder}>
        {isPendingLcDoc && isFailedFile ? (
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={700}>
              Order Creation Failed
            </Typography>
            <Typography variant="body2">{displayCustomerName}</Typography>
          </Stack>
        ) : (
          <Stack spacing={0.75}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {!isPendingLcDoc && (
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 18,
                      color: isSelectedOrder ? 'primary.main' : 'text.secondary',
                    }}
                  />
                )}
                <Typography variant="body2" fontWeight={700}>
                  {isPendingLcDoc ? '' : order?.userOrderId}
                </Typography>
              </Stack>
              {!isPendingLcDoc && order?.status === 'NEW' && (
                <Chip
                  label="New"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Load #: {isPendingLcDoc ? '' : order?.loadNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customer: {displayCustomerName}
            </Typography>
            {displayCreatedDate && (
              <Typography variant="body2" color="text.secondary">
                Created Date: {displayCreatedDate}
              </Typography>
            )}
          </Stack>
        )}
      </OrderCardContent>
      {!isPendingLcDoc && order?.status === 'NEW' && !isDispatch ? (
        <Stack
          direction="row"
          spacing={1}
          sx={actionButtonRowSx}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outlined"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={handleVerifyOrder}
            sx={{
              ...verifyButtonSx,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Edit Order
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteOpen(true)}
            sx={verifyButtonSx}
          >
            Delete Order
          </Button>
        </Stack>
      ) : !isPendingLcDoc &&
        selectedOrderTab !== 'DELIVERED' &&
        selectedOrderTab !== 'ARCHIVED' &&
        !isDispatch ? (
        <Stack
          direction="row"
          spacing={1}
          sx={actionButtonRowSx}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outlined"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={handleEditOrder}
            sx={{
              ...verifyButtonSx,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Edit Order
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteOpen(true)}
            sx={verifyButtonSx}
          >
            Delete Order
          </Button>
        </Stack>
      ) : selectedOrderTab === 'DELIVERED' || selectedOrderTab === 'ARCHIVED' ? (
        <Stack
          direction="row"
          spacing={1}
          sx={actionButtonRowSx}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outlined"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={handleEditOrder}
            sx={{
              ...verifyButtonSx,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            View Order
          </Button>
          {!isDispatch && onRepeatOrder && (
            <Button
              variant="outlined"
              onClick={handleRepeatOrder}
              sx={{
                ...verifyButtonSx,
                borderColor: 'primary.main',
                color: 'primary.main',
              }}
            >
              Repeat Order
            </Button>
          )}
        </Stack>
      ) : (
        <>
          {/* Status row: show for normal orders and for pending LCs that are still processing */}
          {(!isPendingLcDoc || (isPendingLcDoc && !isFailedFile)) && (
            <OrderCardStatus
              direction="row"
              alignItems="center"
              justifyContent={
                isPendingLcDoc
                  ? 'center'
                  : 'flex-start'
              }
              spacing={1}
              sx={{
                bgcolor: isPendingLcDoc
                  ? 'error.main'
                  : 'secondary.light',
              }}
            >
              {isPendingLcDoc ? (
                <Typography variant="body2" fontWeight={700} color="#fff">
                  Processing
                </Typography>
              ) : (
                <>
                  <img
                    src={CircleGreenRevised}
                    alt="check circle green"
                    width={20}
                    height={20}
                  />
                  <Typography variant="body2" fontWeight={700} color="secondary.main">
                    {displayStatus}
                  </Typography>
                </>
              )}
            </OrderCardStatus>
          )}

          {/* Failed temp LC: only show Delete button (no Retry, no extra status bar) */}
          {isPendingLcDoc && isFailedFile && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ padding: '8px 12px 12px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDeletePendingDoc}
                sx={verifyButtonSx}
              >
                Delete
              </Button>
            </Stack>
          )}
        </>
      )}
      <TranspotsAlert
        open={alertVisible}
        handleClose={handleAlertClose}
        handleSubmit={handleAcceptOrder}
        description="Are you sure you want to accept this order?"
        submitBtnText="Accept"
      />
      <ConfirmationDialog
        title="Delete Order"
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        successBtnText="Delete"
        successBtnTextStyle={{ color: 'error' }}
        handleSuccessBtn={handleDeleteOrder}
      >
        Are you sure to delete the order?
      </ConfirmationDialog>
    </OrderCardContainer>
  )
}

export default OrderCard
